import axios from "axios";
import {
  auditTrailData,
  auditStatus,
  auditError,
  continuationToken,
  hasMore,
  currentDays,
  currentView,
  autoLoadEnabled,
  searchTerm,
  isSearching,
} from "./AuditTrailSignals";

// Function to clear audit trail
export const clearAuditTrail = () => {
  auditTrailData.value = [];
  continuationToken.value = null;
  hasMore.value = false;
  currentDays.value = null;
  currentView.value = "topItems";
  autoLoadEnabled.value = false;
  auditStatus.value = "idle";
  searchTerm.value = "";
  isSearching.value = false;
};

// Function to fetch audit trail (top items)
export const getAuditTrail = async ({ maxItemCount = 100, continuationToken: token = null } = {}) => {
  try {
    auditStatus.value = "loading";

    const params = {
      maxItemCount,
    };

    if (token) {
      params.continuationToken = token;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT}/api/AuditTrail/GetAuditTrailTopItems`,
      { params }
    );

    // If there's a continuation token, we're loading more data
    if (token) {
      auditTrailData.value = [...auditTrailData.value, ...response.data.items];
    } else {
      auditTrailData.value = response.data.items;
    }

    continuationToken.value = response.data.continuationToken;
    hasMore.value = response.data.hasMore;
    currentView.value = "topItems";
    currentDays.value = null;
    autoLoadEnabled.value = false;
    isSearching.value = false;
    auditStatus.value = "audit trail loaded";

    return response.data;
  } catch (error) {
    auditStatus.value = "loading audit trail failed";
    auditError.value = error.message;
    throw error;
  }
};

// Function to fetch audit trail by days
export const getAuditTrailByDays = async ({ days, maxItemCount = 100, continuationToken: token = null }) => {
  try {
    auditStatus.value = "loading";

    const params = {
      days,
      maxItemCount,
    };

    if (token) {
      params.continuationToken = token;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT}/api/AuditTrail/GetAuditTrailByDays`,
      { params }
    );

    // If there's a continuation token, we're loading more data
    if (token) {
      auditTrailData.value = [...auditTrailData.value, ...response.data.items];
    } else {
      auditTrailData.value = response.data.items;
      currentDays.value = days;
    }

    continuationToken.value = response.data.continuationToken;
    hasMore.value = response.data.hasMore;
    currentView.value = "byDays";
    isSearching.value = false;
    auditStatus.value = "audit trail loaded";

    // Enable auto-load if there's more data
    if (response.data.hasMore && days) {
      autoLoadEnabled.value = true;
    } else {
      autoLoadEnabled.value = false;
    }

    return response.data;
  } catch (error) {
    auditStatus.value = "loading audit trail failed";
    auditError.value = error.message;
    autoLoadEnabled.value = false;
    throw error;
  }
};

// Function to search audit trail by search term
export const searchAuditTrail = async ({ searchTerm: term, maxItemCount = 100, continuationToken: token = null }) => {
  try {
    auditStatus.value = "searching";

    const params = {
      searchTerm: term,
      maxItemCount,
    };

    if (token) {
      params.continuationToken = token;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT}/api/AuditTrail/SearchAuditTrail`,
      { params }
    );

    // If there's a continuation token, we're loading more data
    if (token) {
      auditTrailData.value = [...auditTrailData.value, ...response.data.items];
    } else {
      auditTrailData.value = response.data.items;
      searchTerm.value = term;
    }

    continuationToken.value = response.data.continuationToken;
    hasMore.value = response.data.hasMore;
    currentView.value = "search";
    currentDays.value = null;
    isSearching.value = true;
    auditStatus.value = "search completed";

    // Enable auto-load if there's more data
    if (response.data.hasMore && term) {
      autoLoadEnabled.value = true;
    } else {
      autoLoadEnabled.value = false;
    }

    return response.data;
  } catch (error) {
    auditStatus.value = "search failed";
    auditError.value = error.message;
    autoLoadEnabled.value = false;
    isSearching.value = false;
    throw error;
  }
};

// Function to reset pagination
export const resetAuditPagination = () => {
  continuationToken.value = null;
  hasMore.value = false;
};

// Function to set current view
export const setCurrentView = (view) => {
  currentView.value = view;
};

// Function to enable/disable auto-load
export const setAutoLoadEnabled = (enabled) => {
  autoLoadEnabled.value = enabled;
};

// Function to clear search
export const clearSearch = () => {
  searchTerm.value = "";
  isSearching.value = false;
  currentView.value = "topItems";
};
